import { Container, SvgGraphics, PointerEvents } from '@tencent/alloy-renderer';
import {
    InterfaceRendererImageComponent,
    InterfaceRendererShapeComponent,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/types';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import LinkImg from '@editor/src/img/canvas/link-flag.svg';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import { reverseComponentFlip } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/reverse-component-flip';
import { CommonEditLogic } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/common-edit-logic';
import { isIframeShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { GraphicsRenderComponent } from '@tencent/alloy-graphics';
import { generateGraphicsModel } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/graphic-adapter/generate-graphics-model';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';

export class ShapeLinkFlagComponent {
    // 图形构造入参
    public factoryOptions: InterfaceFactoryOptions<IPictureModel | IShapeModel>;
    // 链接标识小图标（shape和image设置链接后才有）
    public flagGraphics: Container | null;
    // 基础graphics部分
    public readonly parentComponent: InterfaceRendererImageComponent | InterfaceRendererShapeComponent;
    // 正在编辑链接
    public isEditingLink = false;

    public constructor(
        factoryOptions: InterfaceFactoryOptions<IPictureModel | IShapeModel>,
        parentComponent: InterfaceRendererImageComponent | InterfaceRendererShapeComponent,
    ) {
        this.factoryOptions = factoryOptions;
        this.parentComponent = parentComponent;
        // 事件注册在src/framework/canvas-logic/edit-pc/common-edit-logic.ts
    }

    // 插入|更新|删除 链接标识小图标
    public renderComponent = (): void => {
        const { model, isCroping, fromScale } = this.factoryOptions;
        const { props: { link } } = model;
        if (
            ![UIModelType.SHAPE, UIModelType.IMAGE].includes(model.type)
            || fromScale !== 0
        ) {
            return;
        }

        // 非图片裁剪中 且 （已设置链接 or 设置中 or 插件）则渲染icon  注：插件的icon只是个空component，只是为了事件绑定统一
        if (!isCroping && (link || this.isEditingLink || isIframeShapeModel(model))) {
            this.renderFlagIcon();
        } else if (this.flagGraphics) {
            this.parentComponent.removeChild(this.flagGraphics);
            this.flagGraphics.dispose();
            this.flagGraphics = null;
            // 注销事件
            (this.factoryOptions.logicService.getLogic(this.factoryOptions) as CommonEditLogic)
                ?.flagEventsInstance?.cancelFlagAboutEvents?.();
        }
        if (model.selected) {
            (this.factoryOptions.logicService.getLogic(this.factoryOptions) as CommonEditLogic)
                ?.flagEventsInstance?.registerEnterLinkEdit?.();
        } else {
            (this.factoryOptions.logicService.getLogic(this.factoryOptions) as CommonEditLogic)
                ?.flagEventsInstance?.removeEnterLinkEdit?.();
        }
    };

    // 设置icon到画布上
    private renderFlagIcon = () => {
        const { rectAreaGraphic, iconGraphic } = this.getFlagGraphics();
        if (!this.flagGraphics) {
            // 没有则创建
            this.flagGraphics = new Container();
            if (!isIframeShapeModel(this.factoryOptions.model)) {
                // 插件没有图标
                this.flagGraphics.addChild(rectAreaGraphic);
                this.flagGraphics.addChild(iconGraphic);
            }
            // 逆向翻转
            this.flagGraphics = reverseComponentFlip(this.flagGraphics, this.factoryOptions);
            this.parentComponent.addChild(this.flagGraphics);
            // 绑定事件
            (this.factoryOptions.logicService.getLogic(this.factoryOptions) as CommonEditLogic)
                ?.flagEventsInstance?.registerFlagAboutEvents?.();
        } else if (this.flagGraphics.children.length) {
            // 有则只需要更新尺寸即可
            const sizeCollect = this.getFlagGraphicsSize();
            const { width, height } = sizeCollect.rectAreaGraphicSize;
            const { x, y } = sizeCollect.iconGraphicPosition;
            // 逆向翻转
            this.flagGraphics = reverseComponentFlip(this.flagGraphics, this.factoryOptions);
            // 更新尺寸坐标
            this.flagGraphics.children[0].width = width;
            this.flagGraphics.children[0].height = height;
            this.flagGraphics.children[1].x = x;
            this.flagGraphics.children[1].y = y;
        }
    };

    // 转换graphic数据
    private generateGraphicsModel = (style) => {
        const frameOnSlide = this.factoryOptions.model.getFrame?.().frameOnSlide();
        const realRotateAndFlip = {
            rotate: frameOnSlide.rotate,
            flipH: frameOnSlide.flipH,
            flipV: frameOnSlide.flipV,
        };
        return generateGraphicsModel({ style, factoryOptions: this.factoryOptions, realRotateAndFlip });
    };

    private getFlagGraphicsSize = () => {
        const iconSize = ptToCanvas(22);
        const iconMargin = ptToCanvas(8);
        const backgroundGraphicsSize = { width: iconSize, height: iconSize };
        const rect = this.factoryOptions.frame!;
        const { width, height } = rect;
        let x = width - iconSize - iconMargin;
        let y = height - iconSize - iconMargin;
        // 图标所在图形宽高过小的话，则做居中处理，不再处于右下角
        if (height < iconSize + (iconMargin * 2)) {
            y = (height - iconSize) / 2;
        }
        if (width < iconSize + (iconMargin * 2)) {
            x = (width - iconSize) / 2;
        }
        return {
            backgroundGraphicsSize,
            rectAreaGraphicSize: { width, height },
            iconGraphicPosition: { x, y },
        };
    };

    // 生成图标graphic
    private getFlagGraphics = () => {
        const sizeCollect = this.getFlagGraphicsSize();
        const { width, height } = sizeCollect.rectAreaGraphicSize;
        // 区域撑开（确保本component与wrapper同大小，这样逆向翻转时才正常）
        const rectAreaGraphic = new SvgGraphics(`M 0 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`);
        rectAreaGraphic.pointerEvents = PointerEvents.NONE;

        // icon背景
        const backgroundStyle = {
            ...sizeCollect.backgroundGraphicsSize,
            bgShape: 'ellipse',
            border: {
                color: '#ffffff',
                style: 'solid',
                width: ptToCanvas(1),
            },
            fillBackground: {
                solidFill: {
                    color: { srgb_color: '#0188fbff' },
                },
            },
        };
        const backgroundGraphicsModel = this.generateGraphicsModel(backgroundStyle);
        const backgroundGraphics = new GraphicsRenderComponent(backgroundGraphicsModel);
        // icon图片
        const imgStyle = {
            fillBackground: { image: { src: LinkImg } },
            width: ptToCanvas(12),
            height: ptToCanvas(12),
        };
        const imgGraphicsModel = this.generateGraphicsModel(imgStyle);
        const imgGraphics = new GraphicsRenderComponent(imgGraphicsModel);
        imgGraphics.x = ptToCanvas(5);
        imgGraphics.y = ptToCanvas(5);
        // 组合成icon
        const iconGraphic = new Container();
        iconGraphic.addChild(backgroundGraphics);
        iconGraphic.addChild(imgGraphics);
        iconGraphic.cursor = 'pointer';
        iconGraphic.x = sizeCollect.iconGraphicPosition.x;
        iconGraphic.y = sizeCollect.iconGraphicPosition.y;
        return {
            rectAreaGraphic,
            iconGraphic,
        };
    };
}
